// Timeline helpers — pure functions (no DOM reads). Ported from legacy
// index.html for the React migration (Phase 5 Task 1).
//
// Three categories live here:
//   - getAssetTimelineDuration()  — central duration source (legacy line 5923)
//   - The pin-aware overlap resolver, in three forms:
//       * resolveOverlapsCore<K>() — generic shared algorithm (TTS + STS)
//       * resolveTimelineOverlaps() — TTS Block[] adapter, mutating-style
//       * previewResolveOverlaps()  — TTS dry-run for ghost UI
//       * resolveSTSOverlaps()      — STS MasterClip[] adapter
//
// Snap-direction: all chip durations are unsnapped floats (recorded take
// duration like 2.04s, generated TTS audio with arbitrary decimals). A
// single round-to-nearest at the end of the resolver (snap01) can let
// snapped(prev.offset + prev.dur) round below prev's actual right edge,
// leaving the next chip's snapped left edge inside prev's tail. The core
// uses snapDown for left-side packing (so snapped(left) + dur ≤ rightWall)
// and snapUp for right-side packing + leftOverflow stacking (so
// snapped(left) ≥ leftWall = prev.offset + prev.dur). Pin position keeps
// snap01 to preserve the user's cursor intent.

import type { Block, MasterClip } from '@shared/types';
import { DEFAULT_ASSET_DURATION } from '@shared/constants';
import { resolveBlockVoiceId, resolveTakeVoiceId } from './voiceMigration';

// ---------------------------------------------------------------------------
// Duration source
// ---------------------------------------------------------------------------

/**
 * Central duration source for the timeline (legacy `getAssetTimelineDuration`).
 * Priority:
 *   1) video duration (if loaded)
 *   2) admin-configured `asset.targetDuration`
 *   3) user-entered `customDurations[assetIdx]`
 *   4) loaded music/SFX bed duration (when present — lets the bed audio
 *      define the asset length on no-video assets with no admin/user target)
 *   5) `DEFAULT_ASSET_DURATION` fallback (30 s)
 *
 * The legacy helper returned 0 when nothing was defined. React callers always
 * need something non-zero to render a ruler, so we fall back to the default
 * constant — consistent with how the pre-React UI inserted a sensible
 * fallback via its asset-length control.
 *
 * `bedDuration` is optional so callers that don't yet route the SFX-bed
 * map (e.g. STSMasterTimeline) can omit it without behavior change.
 */
export function getAssetTimelineDuration(
  assetIdx: number,
  videoDurations: Record<number, number>,
  targetDuration: number | null | undefined,
  customDurations: Record<number, number>,
  bedDuration?: number | null | undefined,
): number {
  const vd = videoDurations[assetIdx];
  if (vd && vd > 0) return vd;
  if (targetDuration && targetDuration > 0) return targetDuration;
  const cd = customDurations[assetIdx];
  if (cd && cd > 0) return cd;
  if (bedDuration && bedDuration > 0) return bedDuration;
  return DEFAULT_ASSET_DURATION;
}

// ---------------------------------------------------------------------------
// Snap helpers
// ---------------------------------------------------------------------------

function snap01(x: number): number {
  return Math.round(x * 10) / 10;
}
function snapUp(x: number): number {
  return Math.ceil(x * 10) / 10;
}
function snapDown(x: number): number {
  return Math.floor(x * 10) / 10;
}

// ---------------------------------------------------------------------------
// Shared core resolver — generic over key type K so TTS can use number
// (block index) and STS can use string (master-clip id).
// ---------------------------------------------------------------------------

interface ResolverItem<K> {
  key: K;
  offset: number;
  dur: number;
}

/**
 * Pin-aware overlap resolver core. Pin one item at `pinOffset`; pack the
 * rest around it, snap directions chosen to guarantee non-overlap after
 * grid alignment.
 *
 * Pack strategy (matches legacy lines 7500–7587):
 *   - Pin at 0 → it takes over the 0-anchor role; old left-side items
 *     all become right-side (they can't fit before the pin).
 *   - Adaptive relocation: if leftSide can't fit before pin, evict the
 *     rightmost non-0-anchored item to rightSide. Repeat until it fits or
 *     only 0-anchors remain. Items at offset 0 are protected (legacy
 *     "0-anchor" semantics — user pinned them to start).
 *   - leftSide normal pack: right-to-left from pin.offset, snapDown the
 *     left edge so chip.right ≤ next.left after grid.
 *   - leftOverflow fallback (only 0-anchors left, still won't fit): stack
 *     them from 0 with snapUp at each step, push pin past them.
 *   - rightSide pack: left-to-right from pin.end, snapUp the left edge so
 *     chip.left ≥ prev.right after grid; clamp to maxAllowed.
 *
 * Returns a Map K → new offset for every item (pin included). Items that
 * don't move still appear in the map with their unchanged offset; callers
 * filter for visual ghost rendering but should commit the full map so
 * sub-grid corrections aren't dropped.
 */
function resolveOverlapsCore<K>(
  items: ResolverItem<K>[],
  pinKey: K,
  pinOffset: number,
  totalDuration: number,
  maxAllowedOverride?: number,
): Map<K, number> {
  const maxAllowed =
    maxAllowedOverride ?? (totalDuration > 0 ? totalDuration * 2 : Infinity);
  const out = new Map<K, number>();
  // Local mutable copies — never mutate the caller's items array.
  const work = items.map((it) => ({
    key: it.key,
    offset: it.key === pinKey ? pinOffset : it.offset,
    dur: it.dur,
  }));
  const pin = work.find((i) => i.key === pinKey);
  if (!pin) return out;

  const leftSide = work.filter((i) => i.key !== pinKey && i.offset <= pin.offset);
  const rightSide = work.filter((i) => i.key !== pinKey && i.offset > pin.offset);

  if (pin.offset === 0) {
    while (leftSide.length > 0) rightSide.push(leftSide.shift()!);
  }

  const simulateLeftFit = (): boolean => {
    const sorted = [...leftSide].sort((a, b) => a.offset - b.offset);
    let wall = pin.offset;
    for (let i = sorted.length - 1; i >= 0; i--) {
      const a = sorted[i];
      const want = Math.min(a.offset, wall - a.dur);
      if (want < 0) return false;
      wall = Math.max(0, want);
    }
    return true;
  };
  while (!simulateLeftFit()) {
    leftSide.sort((a, b) => a.offset - b.offset);
    let evictIdx = -1;
    for (let i = leftSide.length - 1; i >= 0; i--) {
      if (leftSide[i].offset !== 0) { evictIdx = i; break; }
    }
    if (evictIdx === -1) break;
    rightSide.push(leftSide.splice(evictIdx, 1)[0]);
  }

  leftSide.sort((a, b) => a.offset - b.offset);
  let rightWall = pin.offset;
  let leftOverflow = false;
  for (let i = leftSide.length - 1; i >= 0; i--) {
    const a = leftSide[i];
    const want = Math.min(a.offset, rightWall - a.dur);
    if (want < 0) leftOverflow = true;
    a.offset = Math.max(0, snapDown(want));
    rightWall = a.offset;
  }

  if (leftOverflow) {
    let cursor = 0;
    leftSide.forEach((a) => {
      a.offset = snapUp(cursor);
      cursor = a.offset + a.dur;
    });
    pin.offset = snapUp(cursor);
  }

  rightSide.sort((a, b) => a.offset - b.offset);
  let leftWall = pin.offset + pin.dur;
  rightSide.forEach((a) => {
    const maxOff = Math.max(0, maxAllowed - a.dur);
    const want = Math.max(a.offset, leftWall);
    a.offset = Math.min(snapUp(want), maxOff);
    leftWall = a.offset + a.dur;
  });

  out.set(pin.key, snap01(pin.offset));
  leftSide.forEach((a) => out.set(a.key, snap01(a.offset)));
  rightSide.forEach((a) => out.set(a.key, snap01(a.offset)));
  return out;
}

// ---------------------------------------------------------------------------
// TTS adapters — Block[] / favIdx model
// ---------------------------------------------------------------------------

interface ActiveChip {
  idx: number;
  offset: number;
  dur: number;
}

function collectActive(blocks: Block[], overrideOffsetForIdx?: { idx: number; offset: number }): ActiveChip[] {
  const out: ActiveChip[] = [];
  blocks.forEach((b, i) => {
    if (b.favIdx < 0 || !b.takes[b.favIdx]) return;
    // Use the raw duration — matches vanilla. A previous ceil-to-0.1
    // quantization here inflated dur by up to 0.099s which faked overlaps
    // against neighbouring offsets stored at their raw values, causing
    // leftOverflow to trip on every first drag after Reset Positions.
    const dur = b.takes[b.favIdx].duration || 0;
    const offset =
      overrideOffsetForIdx && overrideOffsetForIdx.idx === i
        ? overrideOffsetForIdx.offset
        : b.offset || 0;
    out.push({ idx: i, offset: snap01(offset), dur });
  });
  return out;
}

function applyOffsets(
  blocks: Block[],
  nextOffsets: Map<number, number>,
  pinIdx: number,
): Block[] {
  return blocks.map((b, i) => {
    if (!nextOffsets.has(i)) return b;
    const nextOff = nextOffsets.get(i)!;
    const pinned = i === pinIdx ? true : b.pinned;
    if (b.offset === nextOff && b.pinned === pinned) return b;
    return { ...b, offset: nextOff, pinned };
  });
}

/**
 * Pin-aware timeline overlap resolver. Legacy line 7463.
 *
 * - `pinIdx` ≥ 0: the block being dragged/committed. Its current `offset`
 *   is taken at face value; everything else shifts around it without
 *   moving it.
 * - `pinIdx` < 0 or not found: classic left-to-right sweep (non-drag
 *   callers, e.g. timelineSweep helper).
 *
 * Returns a **new** blocks array with updated `offset` values; the pinned
 * block (if any) is flagged `pinned: true`. Other blocks are not mutated.
 *
 * `maxAllowedOverride` ersetzt den 2×-Deckel (Flex-Timeline, Spec 2026-09-14).
 */
export function resolveTimelineOverlaps(
  blocks: Block[],
  pinIdx: number,
  totalDuration: number,
  maxAllowedOverride?: number,
): Block[] {
  const active = collectActive(blocks);
  if (active.length === 0) return blocks;

  const pin = pinIdx >= 0 ? active.find((a) => a.idx === pinIdx) ?? null : null;

  if (!pin) {
    // No-pin sweep — sort and pack left-to-right with snapUp on the
    // collision-resolved left edges so sub-grid durations don't reintroduce
    // overlap after grid alignment.
    active.sort((a, b) => a.offset - b.offset);
    for (let i = 1; i < active.length; i++) {
      const prev = active[i - 1];
      const curr = active[i];
      const prevEnd = prev.offset + prev.dur;
      if (curr.offset < prevEnd) {
        curr.offset = snapUp(prevEnd);
      }
    }
    const maxAllowed =
      maxAllowedOverride ?? (totalDuration > 0 ? totalDuration * 2 : Infinity);
    if (maxAllowed < Infinity) {
      active.forEach((a) => {
        const maxOffset = Math.max(0, maxAllowed - a.dur);
        if (a.offset > maxOffset) a.offset = snap01(maxOffset);
      });
    }
    const offsets = new Map<number, number>();
    active.forEach((a) => offsets.set(a.idx, snap01(a.offset)));
    return applyOffsets(blocks, offsets, -1);
  }

  const items: ResolverItem<number>[] = active.map((a) => ({
    key: a.idx,
    offset: a.offset,
    dur: a.dur,
  }));
  const offsets = resolveOverlapsCore(items, pinIdx, pin.offset, totalDuration, maxAllowedOverride);
  return applyOffsets(blocks, offsets, pinIdx);
}

/**
 * Dry-run of `resolveTimelineOverlaps` (legacy line 7665) — returns the
 * predicted offsets without mutating anything. Used to render dashed
 * ghost outlines during a chip drag so the user can see where other
 * chips will settle on drop.
 *
 * Includes the pin's predicted position too — caller filters by
 * `blockIdx !== pinIdx` for ghost rendering. (The pin position only
 * differs from `pinOffset` in the leftOverflow case where the resolver
 * pushes the dragged chip past stacked left-side siblings.)
 */
export function previewResolveOverlaps(
  blocks: Block[],
  pinIdx: number,
  pinOffset: number,
  totalDuration: number,
): Array<{ blockIdx: number; offset: number }> {
  const active = collectActive(blocks, { idx: pinIdx, offset: pinOffset });
  const pin = active.find((a) => a.idx === pinIdx);
  if (!pin) return [];
  const items: ResolverItem<number>[] = active.map((a) => ({
    key: a.idx,
    offset: a.offset,
    dur: a.dur,
  }));
  const offsets = resolveOverlapsCore(items, pinIdx, snap01(pinOffset), totalDuration);
  const result: Array<{ blockIdx: number; offset: number }> = [];
  offsets.forEach((offset, blockIdx) => result.push({ blockIdx, offset }));
  return result;
}

// ---------------------------------------------------------------------------
// STS adapter — MasterClip[] / id model
// ---------------------------------------------------------------------------

/**
 * MasterClip overlap resolver. Pin one clip at `pinOffset` (the dragged
 * clip's cursor position); pack siblings around it without overlap.
 *
 * `totalDuration` ist die Anzeige-Länge (boundary); ohne Override gilt der
 * 2×-Deckel. `maxAllowedOverride` ersetzt ihn — die Master-Timeline reicht
 * hier ihren `dragCap` durch, wie die TTS-Timeline (Audit M24: vorher kam
 * der schon verdoppelte Deckel als `totalDuration` an und wurde noch einmal
 * verdoppelt, Clips landeten bis 4× boundary).
 *
 * Returns a Map clipId → new offset for every clip (pin included). Caller
 * commits the full map via `setMasterClipOffsets`; the bulk setter no-ops
 * clips whose offset is already correct, so unchanged siblings cost
 * nothing but sub-grid corrections aren't accidentally dropped.
 */
export function resolveSTSOverlaps(
  clips: MasterClip[],
  pinClipId: string,
  pinOffset: number,
  totalDuration: number,
  maxAllowedOverride?: number,
): Map<string, number> {
  const items: ResolverItem<string>[] = clips.map((c) => ({
    key: c.id,
    offset: c.id === pinClipId ? snap01(pinOffset) : snap01(c.offset),
    dur: Math.max(0, c.outPoint - c.inPoint),
  }));
  return resolveOverlapsCore(items, pinClipId, snap01(pinOffset), totalDuration, maxAllowedOverride);
}

// ---------------------------------------------------------------------------
// reorderByOffset / reflowSequentially — keep block-list and timeline in sync
// ---------------------------------------------------------------------------

/**
 * Sort the block array so its index order matches the visual left-to-right
 * order of the chips on the timeline. Fav-bearing blocks come first, sorted
 * by their offset (ties broken by original index for stability). Blocks
 * without a favorite take are appended in their original relative order.
 *
 * Returns `{ blocks, indexMap }` where `indexMap.get(oldIdx) === newIdx` —
 * callers use this to remap things keyed on array index (e.g. activeBlockIdx).
 */
export function reorderByOffset(blocks: Block[]): {
  blocks: Block[];
  indexMap: Map<number, number>;
} {
  const indexed = blocks.map((b, oldIdx) => ({ b, oldIdx }));
  const fav = indexed.filter((x) => x.b.favIdx >= 0 && !!x.b.takes[x.b.favIdx]);
  const rest = indexed.filter((x) => !(x.b.favIdx >= 0 && !!x.b.takes[x.b.favIdx]));
  fav.sort((a, b) => {
    const da = a.b.offset || 0;
    const db = b.b.offset || 0;
    if (da !== db) return da - db;
    return a.oldIdx - b.oldIdx;
  });
  const ordered = [...fav, ...rest];
  const indexMap = new Map<number, number>();
  ordered.forEach((x, newIdx) => indexMap.set(x.oldIdx, newIdx));
  // No-op shortcut: if every block kept its index, return the original ref so
  // React can skip re-renders downstream.
  let same = true;
  for (let i = 0; i < ordered.length; i++) {
    if (ordered[i].oldIdx !== i) { same = false; break; }
  }
  if (same) return { blocks, indexMap };
  return { blocks: ordered.map((x) => x.b), indexMap };
}

/**
 * Re-pack chip offsets sequentially in the current array order. Used by
 * the "Reset Positions" button and after a block-list reorder so the
 * timeline visually mirrors the new order with no gaps.
 *
 * Resets offset + pinned for ALL blocks, including ones without a fav.
 * A block whose fav was removed earlier still carries its stale offset
 * and pinned flag — without clearing them, re-adding the fav after a
 * Reset would skip auto-position and land the chip at its old spot.
 *
 * Each next offset is snapped UP to the 0.1s grid (Math.ceil(x*10)/10) so
 * sub-0.1s adjacency from raw take durations (e.g. 3.07s) never produces
 * pixel-level chip overlap.
 */
export function reflowSequentially(blocks: Block[]): Block[] {
  let cursor = 0;
  return blocks.map((b) => {
    const take = b.favIdx >= 0 ? b.takes[b.favIdx] : null;
    if (!take) {
      if ((b.offset || 0) === 0 && b.pinned === false) return b;
      return { ...b, offset: 0, pinned: false };
    }
    const dur = take.duration || 0;
    const offset = snapUp(cursor);
    const next = b.offset === offset && b.pinned === false
      ? b
      : { ...b, offset, pinned: false };
    cursor = offset + dur;
    return next;
  });
}

// ---------------------------------------------------------------------------
// Multi-Voice support (#173)
// ---------------------------------------------------------------------------

/**
 * Per-Voice-Spur sequenzielles Reflow für "Reset Positions". Jede Spur startet
 * bei t=0 und packt ihre Chips in der Reihenfolge ihrer aktuellen Visual-Offsets
 * lückenlos ab. Cross-Voice-Chips dürfen denselben Offset haben (sie liegen
 * auf unterschiedlichen Spuren).
 *
 * Die Block-Array-Reihenfolge bleibt erhalten (NICHT durch `reorderByOffset`
 * geleitet — das würde Cross-Voice-Reihenfolge willkürlich verschränken).
 * Pro Block wird `offset` neu gesetzt und `pinned` auf false (mirrors
 * `reflowSequentially`-Semantik: Reset = alles entsperrt). Blöcke ohne Take
 * bekommen ebenfalls offset=0, pinned=false (sonst würde ein später wieder
 * gesetztes Favoriten-Take am alten Offset auto-positionieren).
 *
 * Snap-Up Grid wie `reflowSequentially` für Pixel-Parity.
 */
export function resetPositionsPerTrack(
  blocks: Block[],
  assetVoiceIds: string[],
): Block[] {
  if (blocks.length === 0) return blocks;
  const grouped = groupBlocksByVoice(blocks, assetVoiceIds);
  const newOffsetByBlock = new Map<Block, number>();
  for (const [, group] of grouped) {
    // Sort by current visual offset (ties: original order via stable sort).
    const sorted = [...group].sort((a, b) => (a.offset || 0) - (b.offset || 0));
    let cursor = 0;
    for (const b of sorted) {
      const take = b.favIdx >= 0 ? b.takes[b.favIdx] : null;
      const offset = take ? snapUp(cursor) : 0;
      newOffsetByBlock.set(b, offset);
      if (take) cursor = offset + (take.duration || 0);
    }
  }
  // Rebuild in input order — only offset/pinned may change.
  return blocks.map((b) => {
    const newOff = newOffsetByBlock.get(b) ?? 0;
    if ((b.offset || 0) === newOff && b.pinned === false) return b;
    return { ...b, offset: newOff, pinned: false };
  });
}

/** Voice-ID die einem Block-Chip seine Spur zuordnet. Wenn ein Favorit
 *  existiert: dessen voiceId (über resolveTakeVoiceId mit Block-Fallback).
 *  Sonst: block.voiceId (Ghost-Block) mit voiceIds[0]-Fallback. */
export function chipTrackVoiceId(block: Block, assetVoiceIds: string[]): string {
  const blockVoice = resolveBlockVoiceId(block, assetVoiceIds);
  if (block.favIdx < 0) return blockVoice;
  const fav = block.takes[block.favIdx];
  if (!fav) return blockVoice;
  // Veraltete Take-Voice (Admin-Tausch) landet auf der Spur der gültigen
  // Block-Voice statt auf einer unsichtbaren eigenen Spur (Audit M10).
  return resolveTakeVoiceId(fav, blockVoice, assetVoiceIds);
}

/** Distinct voiceIds die aktuell mindestens 1 Chip belegen, in der
 *  Reihenfolge in der sie in asset.voiceIds erscheinen. Stale-voiceIds
 *  (in den Blocks aber nicht in asset.voiceIds) landen am Ende. */
export function distinctActiveVoiceIds(blocks: Block[], assetVoiceIds: string[]): string[] {
  const inUse = new Set<string>();
  for (const b of blocks) inUse.add(chipTrackVoiceId(b, assetVoiceIds));
  const ordered: string[] = [];
  for (const id of assetVoiceIds) if (inUse.has(id)) ordered.push(id);
  for (const id of inUse) if (!ordered.includes(id)) ordered.push(id);
  return ordered;
}

/** Distinct voiceIds aktiver MasterClips, gleiche Reihenfolge wie
 *  `distinctActiveVoiceIds`: asset.voiceIds-Order zuerst, stale ans Ende.
 *  Master-Variante; MasterClip.voiceId ist Phase-1 erforderlich. */
export function distinctActiveVoiceIdsForMaster(
  clips: MasterClip[],
  assetVoiceIds: string[],
): string[] {
  const inUse = new Set<string>();
  for (const c of clips) inUse.add(c.voiceId);
  const ordered: string[] = [];
  for (const id of assetVoiceIds) if (inUse.has(id)) ordered.push(id);
  for (const id of inUse) if (!ordered.includes(id)) ordered.push(id);
  return ordered;
}

/** Map<voiceId, Block[]> für Render und Resolver. */
export function groupBlocksByVoice(blocks: Block[], assetVoiceIds: string[]): Map<string, Block[]> {
  const map = new Map<string, Block[]>();
  for (const b of blocks) {
    const vid = chipTrackVoiceId(b, assetVoiceIds);
    if (!map.has(vid)) map.set(vid, []);
    map.get(vid)!.push(b);
  }
  return map;
}

/** Per-Spur-Resolver. Cross-Track-Overlap explizit erlaubt — Resolver
 *  schaut nur Chips mit gleicher chipTrackVoiceId an. Same-Track-Logik =
 *  bestehender resolveTimelineOverlaps (pin-aware push-on-overlap).
 *
 *  Returns a new Block[] in the SAME order as the input — only `offset`
 *  and `pinned` fields may differ (pinned is set on the pin block by the
 *  inner resolver, mirroring the original behaviour). */
export function resolveTimelineOverlapsPerTrack(
  blocks: Block[],
  assetVoiceIds: string[],
  pinIdx: number = -1,
  totalDuration: number = 0,
  maxAllowedOverride?: number,
): Block[] {
  if (blocks.length === 0) return blocks;
  const grouped = groupBlocksByVoice(blocks, assetVoiceIds);
  // Map block-reference → resolved block (with possibly-updated offset/pinned).
  // We re-assemble in input order to keep activeBlockIdx, IDs etc. stable.
  const resolvedByBlock = new Map<Block, Block>();
  for (const [, group] of grouped) {
    // If the pinned block lives in this group, translate its full-array
    // pinIdx into the group's local index. Otherwise pass -1 (no pin) so
    // this track gets the no-pin sweep.
    const pinBlock = pinIdx >= 0 ? blocks[pinIdx] : undefined;
    const localPinIdx = pinBlock ? group.indexOf(pinBlock) : -1;
    const resolved = resolveTimelineOverlaps(group, localPinIdx, totalDuration, maxAllowedOverride);
    for (let i = 0; i < group.length; i++) {
      resolvedByBlock.set(group[i], resolved[i]);
    }
  }
  return blocks.map((b) => resolvedByBlock.get(b) ?? b);
}

/** Per-Spur-Resolver für STS MasterClips. Mirror von
 *  `resolveTimelineOverlapsPerTrack` aber für `MasterClip[]`. Cross-Track-
 *  Overlap explizit erlaubt — Resolver schaut nur Clips mit gleicher
 *  `voiceId` an. Same-Track-Logik = bestehender `resolveSTSOverlaps`
 *  (pin-aware push-on-overlap).
 *
 *  - `pinClipId === undefined` oder kein Clip mit dieser ID: no-op
 *    (Caller committet nur user-driven Drags — passive Renders triggern
 *    keine Reflows auf der STS Master-Timeline).
 *  - Sonst: Pin's Track wird per `resolveSTSOverlaps` reflowt; andere
 *    Spuren bleiben unverändert.
 *
 *  Returns a new MasterClip[] in the SAME order as the input — only
 *  `offset` may change. Unchanged clips are returned by reference so
 *  React can skip re-renders downstream. */
export function resolveSTSOverlapsPerTrack(
  clips: MasterClip[],
  _assetVoiceIds: string[],
  pinClipId?: string,
  pinOffset: number = 0,
  totalDuration: number = 0,
  maxAllowedOverride?: number,
): MasterClip[] {
  if (clips.length === 0) return clips;
  if (pinClipId === undefined) return clips;
  const pinClip = clips.find((c) => c.id === pinClipId);
  if (!pinClip) return clips;
  // Only the pin's group needs reflow — cross-track overlap is allowed
  // so the other groups stay put. (Mirrors the STS chip-handler intent:
  // user dragged this one clip; only its neighbours on the same voice
  // get pushed.)
  const pinVoiceId = pinClip.voiceId;
  const group = clips.filter((c) => c.voiceId === pinVoiceId);
  const resolved = resolveSTSOverlaps(group, pinClipId, pinOffset, totalDuration, maxAllowedOverride);
  return clips.map((c) => {
    if (c.voiceId !== pinVoiceId) return c;
    const off = resolved.get(c.id);
    if (off === undefined) return c;
    if (Math.abs((c.offset || 0) - off) < 1e-6) return c;
    return { ...c, offset: off };
  });
}
